"use client";

import { useState, useEffect, useRef } from "react";
import { checkSyncStatus, deleteSyncTask } from "@/app/author/actions/author";

const useSyncStatus = (
  authorId,
  setIsSyncing,
  setModalMessage,
  setModalIsOpen,
  setModalIsError,
  isSyncing
) => {
  const intervalId = useRef(null);
  const isMounted = useRef(true);
  const hasMounted = useRef(false); // 👈 para controlar solo el chequeo inicial

  const [progressPropieties, setProgressPropities] = useState({
    title: "Comprobando si hay una sincronización pendiente",
    description: "Espere un momento...",
  });

  const stepMessages = {
    1: "Verificando identificadores...",
    2: "Recuperando información del autor...",
    3: "Buscando autores en la base de datos...",
    4: "Determinando autor principal...",
    5: "Creando autor provisional...",
    6: "Verificando documentos en Redi...",
    7: "Uniendo perfiles...",
    8: "Actualizando fecha de sincronización...",
  };

  const checkSync = async () => {
    if (!isMounted.current) return false;

    const result = await checkSyncStatus(`${authorId}-syncTask`);

    if (!result.success) {
      setIsSyncing(false);
      setModalMessage("Sincronización fallida.");
      setModalIsOpen(true);
      setModalIsError(true);

      try {
        await deleteSyncTask(`${authorId}-syncTask`);
      } catch (error) {
        console.error("Error eliminando la tarea:", error);
      }
      return false;
    }

    if (result.status === "pending") {
      if (result.step && stepMessages[result.step]) {
        setProgressPropities({
          title: "Sincronización en curso",
          description: stepMessages[result.step],
          currentStep: result.step,
          totalSteps: 8,
        });
      }
      return true;
    } else {
      setIsSyncing(false);

      if (result.status === "completed") {
        setModalMessage("Sincronización completada exitosamente");
        setModalIsError(false);
        setModalIsOpen(true);
      } else if (result.status === "failed") {
        setModalMessage("Sincronización fallida.");
        setModalIsError(true);
        setModalIsOpen(true);
      }

      try {
        await deleteSyncTask(`${authorId}-syncTask`);
      } catch (error) {
        console.error("Error eliminando la tarea:", error);
      }
      return false;
    }
  };

  const startCheckingStatus = async () => {
    if (!isMounted.current || intervalId.current) return;

    const shouldContinue = await checkSync();

    if (shouldContinue) {
      setIsSyncing(true); // 👈 solo si hay algo pendiente
      intervalId.current = setInterval(async () => {
        if (!isMounted.current) {
          clearInterval(intervalId.current);
          intervalId.current = null;
          return;
        }

        const continueSyncing = await checkSync();

        if (!continueSyncing) {
          clearInterval(intervalId.current);
          intervalId.current = null;
        }
      }, 1000);
    }
  };

  const stopChecking = () => {
    isMounted.current = false;
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
  };

  // 🟡 1. Chequeo automático inicial al montar
  useEffect(() => {
    isMounted.current = true;

    if (!hasMounted.current) {
      hasMounted.current = true;
      startCheckingStatus(); // ← esto detecta si hay algo pendiente
    }

    return () => {
      stopChecking();
    };
  }, []);

  // 🟢 2. Chequeo cuando el usuario hace clic en sincronizar
  useEffect(() => {
    if (isSyncing) {
      startCheckingStatus();
    }
  }, [isSyncing]);

  return { stopChecking, progressPropieties };
};

export default useSyncStatus;
